import { Button, Space, Accordion, Card, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { useMemo, createContext, useContext, useState, useEffect } from 'react';

import { isProduction } from 'shared/lib';

import styles from './CreditSelectionDevTool.module.scss';

const { Text } = Typography;

type TDevTool = {
  type: 'button' | 'input' | 'select';
  label: string;
  id: string;
  onClick?: () => void;
};

const DevToolContext = createContext<{
  addTool: (tool: TDevTool | TDevTool[]) => void;
  removeTool: (toolId: string | string[]) => void;
  tools: TDevTool[] | null;
} | null>(null);

export type TDevToolContext = NonNullable<typeof DevToolContext extends React.Context<infer T> ? T : never>;

export const useDevTool = () => {
  const context = useContext(DevToolContext);
  if (!context) {
    return null;
  }
  return context;
};

export const CreditSelectionDevTool: FC = () => {
  const toolsContext = useContext(DevToolContext);

  if (!toolsContext) {
    return null;
  }

  const { tools } = toolsContext;

  return (
    <Card
      className={styles.devToolPopoverContainer}
      variant="outlined"
      color="blue"
      shadow
    >
      <Accordion>
        <Accordion.Item
          title={<Text size={14}>DevTool</Text>}
          unmountOnExit={false}
          className={styles.accordionItem}
        >
          <Space
            direction="vertical"
            size={4}
          >
            {tools?.length
              ? tools?.map((tool) => {
                  if (tool.type === 'button') {
                    return (
                      <Button
                        key={tool.id}
                        variant="secondary"
                        type="button"
                        onClick={tool.onClick}
                        size={36}
                        block
                      >
                        <Text size={12}>{tool.label}</Text>
                      </Button>
                    );
                  }
                  return null;
                })
              : 'No tools available'}
          </Space>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};

export const CreditSelectionDevToolProvider: FC = ({ children }) => {
  const [isShowDevTool, setIsShowDevTool] = useState(false);
  const [tools, setTools] = useState<TDevTool[] | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (typeof window === 'object' && !isProduction()) {
      setIsShowDevTool(!localStorage.getItem('hideDevTool'));

      (window as any).hideDevTool = () => {
        localStorage.setItem('hideDevTool', 'true');
        setIsShowDevTool(false);
        // eslint-disable-next-line no-console
        console.log('%c👌', 'font-size: 30px');
      };

      (window as any).showDevTool = () => {
        localStorage.removeItem('hideDevTool');
        setIsShowDevTool(true);
        // eslint-disable-next-line no-console
        console.log('%c👌', 'font-size: 30px');
      };

      if (!sessionStorage.getItem('devToolInitLogged')) {
        // eslint-disable-next-line no-console
        console.log(
          '%c[DevTool] Functions initialized: hideDevTool(), showDevTool(). Added to window object',
          'color: #4CAF50; font-weight: bold;',
        );
        sessionStorage.setItem('devToolInitLogged', 'true');
      }
    }
  }, []);

  const contextValue: TDevToolContext = useMemo(
    () => ({
      tools,
      removeTool: (toolId: string | string[]) => {
        setTools(
          (prev) =>
            prev?.filter((tool) => (Array.isArray(toolId) ? !toolId.includes(tool.id) : tool.id !== toolId)) || null,
        );
      },
      addTool: (tool: TDevTool | TDevTool[]) => {
        setTools((prev) => {
          const toolsArray = Array.isArray(tool) ? tool : [tool];
          const existingIds = new Set((prev || []).map((t) => t.id));
          const newTools = toolsArray.filter((t) => !existingIds.has(t.id));
          return [...(prev || []), ...newTools];
        });
      },
    }),
    [setTools, tools],
  );

  return (
    <DevToolContext.Provider value={contextValue}>
      {!isMobile && isShowDevTool && <CreditSelectionDevTool />}
      {children}
    </DevToolContext.Provider>
  );
};
